'use client';

import { useState } from 'react';
import { Search, ShieldAlert, Loader2, ArrowRight } from 'lucide-react';
import { platformLink } from '@/lib/config';

export default function FreeScanSection() {
  const [url, setUrl] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  function normalizeUrl(value: string): string | null {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const withScheme = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    try {
      const parsed = new URL(withScheme);
      if (!parsed.hostname.includes('.')) return null;
      return parsed.toString();
    } catch {
      return null;
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    const normalized = normalizeUrl(url);
    if (!normalized) {
      setError('Enter a valid URL, e.g. https://your-app.com');
      return;
    }
    setLoading(true);
    window.location.href = platformLink.registerWithScan(normalized);
  }

  return (
    <section id="scan" className="sm:px-6 lg:px-8 sm:py-20 max-w-5xl mx-auto py-14 px-4">
      <div className="relative rounded-none border border-white/10 bg-neutral-900/40 overflow-hidden p-8 sm:p-12">
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-rose-500/10 via-neutral-900/0 to-transparent"></div>
        </div>

        <div className="text-center mb-8">
          <span className="inline-flex items-center gap-2 text-sm font-medium text-rose-400 px-3 py-1 rounded-none bg-rose-500/10 border border-rose-500/20">
            <ShieldAlert className="w-3.5 h-3.5" />
            Free Security Scan
          </span>
          <h2 className="mt-4 text-3xl sm:text-4xl font-semibold tracking-tight text-white">
            See your app through an attacker&apos;s eyes
          </h2>
          <p className="mt-3 text-base text-neutral-400 max-w-2xl mx-auto">
            Drop in your URL. We run a read-only scan of what&apos;s publicly visible - TLS, headers, exposed API docs, cookie settings - and send a plain-language report. No install, no exploit payloads.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="max-w-xl mx-auto">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
              <input
                type="text"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://your-app.com"
                className="w-full h-12 pl-11 pr-4 rounded-none bg-black/50 border border-white/10 text-white text-sm placeholder:text-neutral-600 focus:outline-none focus:border-rose-500/50 transition-colors"
                aria-label="Your app URL"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center justify-center h-12 px-7 rounded-none bg-white text-black text-sm font-semibold hover:bg-neutral-200 transition-colors gap-2 disabled:opacity-60 shrink-0"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Scan now <ArrowRight className="w-4 h-4" /></>}
            </button>
          </div>
          {error && <p className="mt-3 text-sm text-rose-400 text-center">{error}</p>}
          <p className="mt-4 text-xs text-neutral-500 text-center">
            Free passive scan after a quick sign-up. A full active pentest runs once you verify domain ownership - we never attack sites you don&apos;t own.
          </p>
        </form>
      </div>
    </section>
  );
}
